import React, {useCallback, useEffect, useMemo, useState} from 'react'
import ListPage from '../../Components/List/ListPage'
import CommentsItem from '../../Components/List/Item/CommentsItem'
import DropdownGroup from '../../Components/Dropdown/DropdownGroup'
import DropdownItem from '../../Components/Dropdown/DropdownItem'
import {ColInfo, ColOptions, ColPhoto, ColSelect, ColStat,} from '../../Components/List/Col'
import Dropdown from '../../Components/Dropdown'
import {ButtonIcon} from '../../Components/Button/Button'
import styles from '../../../CSS/main.css'
import {useNavigation} from '../../utils/navigation'
import {useListState} from '../../Components/List/hooks'
import {Filters} from '../../Components/Filters'
import {COMMENT_FILTERS, COMMENT_STATE, COMMENT_TABS, INIT_SEARCH_STATE, INIT_STATE, SearchPagination} from './filters'
import {AdminCommentsTemplateData} from "../../../../types";
import {getAllWebsites, getComments, getParentCommentById, postCommentAction, searchComments} from "../../api/requests";
import {useAsync, useAsyncCallback} from "react-async-hook";
import { Routes } from '../../routing'
import { showError } from '../../Components/Toast'

const Comments: React.VFC = () => {
  const { goBack, goToNewTab, goTo } = useNavigation()

  const {
    params,
    setActiveTab,
    onPageChange,
    select,
    toggleAll,
    selectedItems,
    onChangeSearch,
    setListParams,
    resetState,
    items,
    pageCount,
    refetch,
    loading,
    commentCount,
  } = useListState<AdminCommentsTemplateData, COMMENT_STATE>(INIT_STATE, getComments)

  const [dataSearch, setDataSearch] = useState<ReadonlyArray<AdminCommentsTemplateData>>([])
  const [dataSearchSelected, setDataSearchSelected] = useState<ReadonlyArray<AdminCommentsTemplateData>>([])

  const [isResetSearch, setIsResetSearch] = useState<boolean>(true)
  const [searchPagination, setSearchPagination] = useState<SearchPagination>(INIT_SEARCH_STATE)
  const onSearchPageChange = useCallback((page: number) => {
    setSearchPagination((prevState) => ({
      ...prevState,
      currentPage: page,
    }))
  }, [searchPagination])
  const { page, tab, searchTerm } = params

  const websitesFetcher = useAsync(getAllWebsites, [])

  const filters = useMemo(() => {
    const sites = (websitesFetcher.result?.rows || []).map(r => ({name: r.name, value: r.name}))
    return COMMENT_FILTERS(sites)
  }, [websitesFetcher.result])

  const performAction = useAsyncCallback(postCommentAction)
  const onAction = useCallback(async (type: string, ids: ReadonlyArray<number>) => {
    await performAction.execute(type, ids)
    refetch()
  }, [refetch])

  const onApprove = useCallback(async (ids: ReadonlyArray<number>) => {
    await onAction('approve', ids)
    if (!isResetSearch) {
      const dataUpdated = dataSearch.map((item) => {
        if (ids.includes(item.id)) {
          return {...item, approved: true, rejected: false}
        }
        return item
      })
      setDataSearch([...dataUpdated])
    }
  }, [onAction, isResetSearch, dataSearch])

  const onBlock = useCallback(async (ids: ReadonlyArray<number>) => {
    await onAction('block', ids)
    if (!isResetSearch) {
      const dataUpdated = dataSearch.map((item) => {
        if (ids.includes(item.id)) {
          return {...item, approved: false, rejected: true}
        }
        return item
      })
      setDataSearch([...dataUpdated])
    }
  }, [onAction, isResetSearch, dataSearch])

  const onDelete = useCallback(async (ids: ReadonlyArray<number>) => {
    await onAction('delete', ids)
    if (!isResetSearch) {
      const dataUpdated = dataSearch.filter((item) => !ids.includes(item.id))
      setDataSearch([...dataUpdated])
    }
  }, [onAction, isResetSearch, dataSearch])

  const handleChangeStatus = useCallback(async (id: number, type: string, parentId: number | null) => {
    if (parentId) {
      const checkParent = await getParentCommentById(parentId)
      if (!checkParent) {
        onDelete([id])
        return showError('This comment was deleted')
      }
    }
    return type === 'approve' ? onApprove([id]) : onBlock([id])
  }, [onDelete, onApprove, onBlock])

  const handleCommentSearch = useCallback(async (keyword: string, tab: string, currentPage: number) => {
    const result = await searchComments(keyword, tab, currentPage)
    setDataSearch(result.rows)
    setSearchPagination({
      keyword,
      currentPage,
      totalPage: Math.ceil(result.count / 20),
      commentCount: result.count
    })
  }, [tab, searchPagination])

  const handleSearch = useCallback(async (keyword: string) => {
    if (!keyword) {
      setIsResetSearch(true)
      setSearchPagination(INIT_SEARCH_STATE)
      setDataSearch([])
      return
    }
    setIsResetSearch(false)
    handleCommentSearch(keyword, tab, 1)
  }, [tab])

  useEffect(()=> {
    if(isResetSearch) {
      onPageChange(1)
    } else if (searchPagination.keyword) {
      handleCommentSearch(searchPagination.keyword, tab, searchPagination.currentPage)
      setDataSearchSelected([])
    }
  },[searchPagination.currentPage, isResetSearch])

  const dataComment = isResetSearch ? items : dataSearch
  const dataSelected = isResetSearch ? selectedItems : dataSearchSelected
  const allDataSelected = dataComment.length ? (dataSelected.length === dataComment.length) : false

  const handleSelect = useCallback((item) => {
    if (isResetSearch) {
      select(item)
    } else {
      setDataSearchSelected(
        dataSearchSelected.includes(item)
          ? dataSearchSelected.filter((i) => i !== item)
          : [...dataSearchSelected, item]
      )
    }
  }, [select, isResetSearch, allDataSelected, dataSearch, dataSearchSelected])

  const handleSelectAll = useCallback(() => {
    if (isResetSearch) {
      toggleAll()
    } else {
      setDataSearchSelected(allDataSelected ? [] : dataSearch)
    }
  }, [toggleAll, isResetSearch, allDataSelected, dataSearch])

  return (
    <ListPage
      loading={loading}
      items={dataComment}
      searchTerm={searchTerm}
      isResetSearch={isResetSearch}
      onChangeSearch={onChangeSearch}
      onSearch={handleSearch}
      filters={(toggle) => (
        <Filters
          filters={filters}
          filterState={params}
          updateParams={setListParams}
          onReset={resetState}
          onClose={toggle}
        />
      )}
      itemRenderer={(i) => (
        <CommentsItem
          key={i.id}
          onSelected={() => handleSelect(i)}
          selected={dataSelected.includes(i)}
          comment={i}
          onApprove={() => handleChangeStatus(i.id, 'approve', i.targetTo || i.replyTo)}
          onBlock={() => handleChangeStatus(i.id, 'block', i.targetTo || i.replyTo)}
          isDeleted={tab !== 'deleted'}
          options={ tab !== 'deleted' &&
            <>
              <DropdownGroup>
                <DropdownItem onClick={()=> goToNewTab(i.origin)}>Go to URL</DropdownItem>
              </DropdownGroup>
              <DropdownGroup>
                <DropdownItem onClick={() => handleChangeStatus(i.id, 'approve', i.targetTo || i.replyTo)}>Approve Comment</DropdownItem>
                <DropdownItem onClick={() => handleChangeStatus(i.id, 'block', i.targetTo || i.replyTo)}>Block Comment</DropdownItem>
              </DropdownGroup>
            </>
          }
        />
      )}
      headerRenderer={() => (
        tab !== 'deleted' &&
        <>
          <ColSelect
            onChange={handleSelectAll}
            showActions={!!dataSelected.length}
            toggle={allDataSelected}>
            <Dropdown
              trigger={
                <ButtonIcon
                  title='Select Options'
                  additionalClassNames={[styles.SelectOptions]}
                />
              }
              position={'bottom'}>
              <DropdownGroup>
                <DropdownItem onClick={() => onApprove(dataSelected.map(i => i.id))}>Approve selected</DropdownItem>
                <DropdownItem onClick={() => onBlock(dataSelected.map(i => i.id))}>Block selected</DropdownItem>
              </DropdownGroup>
              <DropdownGroup>
                <DropdownItem highlighted onClick={() => onDelete(dataSelected.map(i => i.id))}>
                  Delete selected
                </DropdownItem>
              </DropdownGroup>
            </Dropdown>
          </ColSelect>
          <ColPhoto />
          <ColInfo>
            <a href='#'>Username</a>
          </ColInfo>
          <ColStat>
            <a href='#'>Date</a>
          </ColStat>
          <ColOptions />
        </>
      )}
      title={'Manage Comments'}
      onBack={goBack}
      tabs={COMMENT_TABS}
      activeTab={tab}
      onChangeTab={setActiveTab}
      commentCount= {isResetSearch ? commentCount : searchPagination.commentCount}
      pageCount={isResetSearch ? pageCount : searchPagination.totalPage}
      page={isResetSearch ? page : searchPagination.currentPage}
      onPageChange={isResetSearch ? onPageChange : onSearchPageChange}
      isShowSearch
      searchType='Comments'
      dataSelectedCount = {dataSelected.length}
    />
  )
}

export default Comments
